import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../partage/api/client';
import { cles } from '../../partage/relectures';
import type { PossibleDuJour, ReglagesDeProposition, TacheDeJournee } from '../../partage/api/types';
import {
  AJOUTER_UNE_TACHE,
  C_EST_MA_JOURNEE,
  FINIRAIT_APRES_MINUIT,
  GESTES_DE_LA_PROPOSITION,
  JE_TE_PROPOSE,
  LA_QUESTION,
  remplacerDansLaJournee,
  REPLIER_LA_LISTE,
  RIEN_NE_TIENT_ICI,
  retirerDeLaJournee,
} from '../../partage/vocabulaire';
import { CreneauxDeLaJournee, CroixDuRetrait } from './CreneauxDeLaJournee';
import { dureeLisible } from './datesDeLAgenda';
import { faitesGardees } from './journeeDuMoment';
import { tempsDeLaJournee } from './journeeEnCours';
import { ListeAuChoix } from './ListeAuChoix';
import { ceQuEstLaTache, choixDeLaProposition, debloquantDe, ecarter, remplacerDans, type TacheProposee } from './propositionDuJour';
import { resumeDesReponses, type Reponses } from './questionDuJour';
import { TempsPris } from './TempsDeLaJournee';
import { enMots } from './refusDuJour';
import { useJourneeAComposer } from './useJourneeAComposer';
import './LaQuestion.css';
import './PreparerUnJour.css';
import './ProposeMoi.css';

/**
 * « Propose-moi une journée », au téléphone (chantier `telephone-journee-agenda`, ticket 02,
 * décisions d'Igor du 2026-10-05, 8, 9 et 20 ; maquette `design/telephone.html`, piste
 * « Journée », « Je te propose »).
 *
 * L'app remplit le temps répondu, le débloquant en tête, chaque tâche à son heure ; le rendez-vous
 * du jour reste à la sienne, sur l'ambre, sans × ni ↻. × enlève une tâche (les heures suivent) ;
 * ↻ la remplace par une autre qui tient dans le même temps ; « + Ajouter une tâche » ouvre la liste
 * de « Je choisis moi-même » (ses trois boutons, ticket 03) ; toucher une heure la fixe, comme dans
 * « Préparer ma journée ».
 *
 * C'est une possibilité, pas imposée (Igor, décision 8) : rien ne s'écrit avant « C'est ma
 * journée », et « ‹ La question » revient sans rien écrire. Le serveur propose, remplace et range ;
 * l'écran garde la liste et dit ce que chaque tâche est (`propositionDuJour.ts`).
 */
export function ProposeMoi({
  jour,
  reponses,
  maintenant,
  onRetour,
  onFermer,
  faites = [],
}: {
  jour: string;
  reponses: Reponses;
  /** L'heure qu'il est, arrondie par le serveur ; aujourd'hui seulement. */
  maintenant: string | null;
  onRetour: () => void;
  onFermer: (message: string) => void;
  /** Les tâches déjà faites aujourd'hui, gardées à leur heure (« Recommencer », ticket 04). */
  faites?: readonly TacheDeJournee[];
}) {
  const reglages: ReglagesDeProposition = {
    debut: reponses.debut,
    tempsMin: reponses.tempsMin,
    ou: reponses.ou,
    courage: reponses.courage,
  };
  const { tempsMin } = reglages;

  // Une proposition neuve à chaque ouverture : rien n'en est gardé une fois l'écran quitté.
  const proposition = useQuery({
    queryKey: cles.proposition(jour, reglages),
    queryFn: () => api.proposerUnJour(jour, reglages),
    staleTime: Infinity,
    gcTime: 0,
    retry: false,
  });

  const [gardees] = useState(() => faitesGardees(faites));
  const faitesIci = new Set(gardees.map((c) => c.id));
  const journee = useJourneeAComposer({ jour, reglages, onFermer, gardees, rangementFige: true });
  const { choix, liste, demarrer } = journee;
  const [connues, setConnues] = useState<Map<string, TacheProposee>>(() => new Map());
  const [ecartees, setEcartees] = useState<string[]>([]);
  const [ajout, setAjout] = useState(false);
  const [remplacement, setRemplacement] = useState<string | null>(null);

  // La proposition reçue devient la journée en cours, une fois ; ses heures servent tout de suite.
  const recue = proposition.data;
  const pasEncore = choix === null;
  useEffect(() => {
    if (!recue || !pasEncore) return;
    setConnues(new Map(recue.taches.map((t) => [t.id, t])));
    demarrer(choixDeLaProposition(recue.taches), { creneaux: recue.creneaux, tientDansLeJour: recue.tientDansLeJour });
    // `demarrer` change à chaque rendu : hors des dépendances, la proposition ne se pose qu'une fois.
  }, [recue, pasEncore]);

  const { utilise, reste } = tempsDeLaJournee(liste, [...connues.values()].map((t) => [t.id, t.minutes] as const), tempsMin);
  const debloquant = debloquantDe(liste.map((c) => connues.get(c.id)).filter((t): t is TacheProposee => !!t));

  function enlever(id: string) {
    setEcartees((avant) => ecarter(avant, id));
    journee.retirerLaTache(id);
  }

  /** ↻ : le serveur cherche, dans le même ordre, une autre tâche qui tient à cette place. */
  async function remplacer(id: string) {
    journee.setEnHeure(null);
    setRemplacement(id);
    try {
      const r = await api.remplacerDansLaProposition(jour, {
        ...reglages,
        taches: liste.map((c) => c.id),
        remplacer: id,
        ecartees,
      });
      setConnues((avant) => new Map(avant).set(r.remplacant.id, r.remplacant));
      setEcartees((avant) => ecarter(avant, id));
      journee.toucher(remplacerDans(liste, id, r.remplacant.id));
    } catch (e) {
      journee.setMessage(enMots(e));
    } finally {
      setRemplacement(null);
    }
  }

  function ajouterLaTache(p: PossibleDuJour) {
    if (journee.ajouterLaTache(p, reste)) setConnues((avant) => (avant.has(p.id) ? avant : new Map(avant).set(p.id, p)));
  }

  const tete = (
    <>
      <button type="button" className="planifier-retour" onClick={onRetour}>
        ‹ {LA_QUESTION}
      </button>
      <h2 className="planifier-titre">{JE_TE_PROPOSE}</h2>
      <p className="planifier-resume">{resumeDesReponses(reponses, maintenant)}</p>
    </>
  );

  if (proposition.error) {
    return (
      <section className="planifier-plein propose" data-etape="propose" aria-label={JE_TE_PROPOSE}>
        {tete}
        <p className="propose-message" role="status">
          {enMots(proposition.error)}
        </p>
      </section>
    );
  }

  return (
    <section className="planifier-plein propose" data-etape="propose" data-pret={choix ? 'oui' : 'non'} aria-label={JE_TE_PROPOSE}>
      {tete}
      {!choix ? (
        <p className="propose-note">…</p>
      ) : (
        <>
          {liste.length === 0 && <p className="propose-note">{RIEN_NE_TIENT_ICI}</p>}
          {liste.length > 0 && journee.creneaux.length === 0 && <p className="propose-note">…</p>}
          <CreneauxDeLaJournee
            creneaux={journee.creneaux}
            faites={faitesIci}
            enHeure={journee.enHeure}
            onOuvrirLHeure={(id) => {
              journee.setMessage(null);
              journee.setEnHeure(id);
            }}
            onQuitterLHeure={() => journee.setEnHeure(null)}
            onFixer={(id, h) => void journee.fixerLHeure(id, h)}
            quoi={(c) => {
              const t = connues.get(c.id);
              return t ? ceQuEstLaTache(t, debloquant) : dureeLisible(c.minutes);
            }}
            gestes={(c) => (
              <>
                <button
                  type="button"
                  className="preparer-geste propose-geste"
                  aria-label={remplacerDansLaJournee(c.titre)}
                  data-geste="remplacer"
                  disabled={remplacement !== null}
                  onClick={() => void remplacer(c.id)}
                >
                  <span aria-hidden="true">↻</span>
                </button>
                <button
                  type="button"
                  className="preparer-geste propose-geste"
                  aria-label={retirerDeLaJournee(c.titre)}
                  data-geste="retirer"
                  disabled={remplacement !== null}
                  onClick={() => enlever(c.id)}
                >
                  <CroixDuRetrait />
                </button>
              </>
            )}
          />
          {liste.length > 0 && <p className="propose-note">{GESTES_DE_LA_PROPOSITION}</p>}

          <button type="button" className="propose-plus" aria-expanded={ajout} data-geste="ajouter" onClick={() => setAjout(!ajout)}>
            {ajout ? REPLIER_LA_LISTE : `${AJOUTER_UNE_TACHE} ›`}
          </button>
          {ajout && (
            <ListeAuChoix
              jour={jour}
              ou={reglages.ou}
              courage={reglages.courage}
              pris={liste.map((c) => c.id)}
              reste={reste}
              onAjouter={ajouterLaTache}
            />
          )}

          <footer className="propose-pied">
            {journee.message && (
              <p className="propose-message" role="status">
                {journee.message}
              </p>
            )}
            {journee.apresMinuit && (
              <p className="propose-message" role="status">
                {FINIRAIT_APRES_MINUIT}
              </p>
            )}
            <p className="propose-compteur">
              <TempsPris utilise={utilise} tempsMin={tempsMin} />
            </p>
            <button
              type="button"
              className="preparer-fort"
              disabled={!journee.pret || remplacement !== null}
              onClick={() => journee.enregistrer.mutate()}
            >
              {C_EST_MA_JOURNEE}
            </button>
          </footer>
        </>
      )}
    </section>
  );
}
